import { getAllPatients } from "./patientService";
import { getInvoiceList } from "./invoiceService";
import { getAllMedicineTransactions } from "./medicineTransactionService";
import { getAllReports } from "./pathLabTestReportService";

const toTimestamp = (value) => {
  if (!value) return null;
  const t = new Date(value).getTime();
  return Number.isFinite(t) ? t : null;
};

const patientDisplayName = (patient) =>
  patient?.name ||
  `${patient?.firstName || ""} ${patient?.lastName || ""}`.trim() ||
  "a patient";

const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString("en-IN")}`;

/**
 * Synthesizes a "recent activity" feed by pulling the last events from
 * four existing services (patients, invoices, medicine transactions, lab
 * reports) and merging them by their real timestamp fields — there is no
 * backend activity/audit log to read from directly. Consultation/prescription
 * completion is intentionally left out: the only prescription endpoints are
 * scoped per-doctor, and fetching per-doctor on every dashboard load isn't
 * worth the request fan-out for one more category.
 */
export const getRecentActivity = async (limit = 5) => {
  const [patients, invoices, medicineTx, labReports] = await Promise.allSettled([
    getAllPatients(),
    getInvoiceList({ pageSize: 20 }),
    getAllMedicineTransactions(),
    getAllReports(),
  ]);

  const events = [];

  if (patients.status === "fulfilled" && Array.isArray(patients.value)) {
    patients.value.forEach((patient) => {
      const timestamp = toTimestamp(patient?.createdAt || patient?.created_at);
      if (timestamp) {
        events.push({
          type: "patient",
          label: `New patient registered — ${patientDisplayName(patient)}`,
          color: "#2563eb",
          timestamp,
        });
      }
    });
  }

  if (invoices.status === "fulfilled") {
    const list = Array.isArray(invoices.value)
      ? invoices.value
      : invoices.value
        ? [invoices.value]
        : [];
    list.forEach((invoice) => {
      const timestamp = toTimestamp(invoice?.date || invoice?.createdAt || invoice?.created_at);
      if (timestamp) {
        events.push({
          type: "payment",
          label: `Payment received — ${formatCurrency(invoice?.totalAmount)}`,
          color: "#7c3aed",
          timestamp,
        });
      }
    });
  }

  if (medicineTx.status === "fulfilled" && Array.isArray(medicineTx.value)) {
    medicineTx.value
      .filter((tx) => String(tx?.transactionType || "").toUpperCase() === "OUT")
      .forEach((tx) => {
        const timestamp = toTimestamp(tx?.transactionDate);
        if (timestamp) {
          events.push({
            type: "medicine",
            label: `Medicine issued — ${tx?.medicineName || "Medicine"}`,
            color: "#ea580c",
            timestamp,
          });
        }
      });
  }

  if (labReports.status === "fulfilled" && Array.isArray(labReports.value)) {
    labReports.value.forEach((report) => {
      const timestamp = toTimestamp(report?.reportDate || report?.report_date);
      if (timestamp) {
        events.push({
          type: "lab",
          label: `Lab report updated — ${report?.testName || report?.test_name || "Test"}`,
          color: "#db2777",
          timestamp,
        });
      }
    });
  }

  events.sort((a, b) => b.timestamp - a.timestamp);
  return events.slice(0, limit);
};
